/**
 * Semántica de los movimientos.
 *
 * Aquí vive la corrección contable más importante del proyecto. Cada tipo de
 * movimiento responde a dos preguntas que **Cuentia respondía mal**:
 *
 *   1. ¿Mueve el saldo de una cuenta?  (`affectsCash`)
 *   2. ¿Cuenta como gasto o ingreso nuevo en los reportes?
 *      (`countsAsExpense`, `countsAsIncome`)
 *
 * El error que se corrige: en Cuentia, pagar el fiado y abonar a una deuda **no
 * tocaban el saldo de la cuenta**. El resultado era que el saldo disponible, los
 * activos y el patrimonio salían inflados, y el copiloto afirmaba cifras que no
 * eran ciertas.
 *
 * Y la regla de oro que sí se conserva: pagar el fiado o abonar a una deuda
 * **mueve la caja pero nunca cuenta como gasto nuevo**. Si no, el mismo dinero
 * se contaría dos veces: una al comprar y otra al pagar.
 */
const SEMANTICS = {
    EXPENSE: {
        code: 'EXPENSE',
        label: 'Gasto',
        description: 'Sale dinero de una cuenta.',
        affectsCash: true,
        affectsCounterAccount: false,
        countsAsExpense: true,
        countsAsIncome: false,
        debtEffect: 0,
        requiresCategory: true,
        requiresCounterAccount: false,
        requiresDirection: false,
    },
    INCOME: {
        code: 'INCOME',
        label: 'Ingreso',
        description: 'Entra dinero a una cuenta.',
        affectsCash: true,
        affectsCounterAccount: false,
        countsAsExpense: false,
        countsAsIncome: true,
        debtEffect: 0,
        requiresCategory: true,
        requiresCounterAccount: false,
        requiresDirection: false,
    },
    TRANSFER: {
        code: 'TRANSFER',
        label: 'Transferencia',
        description: 'Mueve dinero entre dos cuentas tuyas. No es gasto ni ingreso.',
        affectsCash: true,
        affectsCounterAccount: true,
        countsAsExpense: false,
        countsAsIncome: false,
        debtEffect: 0,
        requiresCategory: false,
        requiresCounterAccount: true,
        requiresDirection: false,
    },
    FIADO_PURCHASE: {
        code: 'FIADO_PURCHASE',
        label: 'Compra fiada',
        description: 'Cuenta como gasto del mes, pero el dinero todavía no ha salido: sube lo que debes en la tienda.',
        affectsCash: false,
        affectsCounterAccount: false,
        countsAsExpense: true,
        countsAsIncome: false,
        debtEffect: 1,
        requiresCategory: true,
        requiresCounterAccount: false,
        requiresDirection: false,
    },
    FIADO_PAYMENT: {
        code: 'FIADO_PAYMENT',
        label: 'Abono al fiado',
        description: 'Sale dinero de tu cuenta y baja lo que debes. Nunca cuenta como gasto nuevo.',
        affectsCash: true,
        affectsCounterAccount: false,
        countsAsExpense: false,
        countsAsIncome: false,
        debtEffect: -1,
        requiresCategory: false,
        requiresCounterAccount: false,
        requiresDirection: false,
    },
    DEBT_PAYMENT: {
        code: 'DEBT_PAYMENT',
        label: 'Abono a capital',
        description: 'Baja lo que debes. Mueve la caja, pero no es un gasto nuevo.',
        affectsCash: true,
        affectsCounterAccount: false,
        countsAsExpense: false,
        countsAsIncome: false,
        debtEffect: -1,
        requiresCategory: false,
        requiresCounterAccount: false,
        requiresDirection: false,
    },
    DEBT_INTEREST: {
        code: 'DEBT_INTEREST',
        label: 'Intereses y seguros',
        description: 'Intereses, seguros, cuota de manejo y comisiones de una deuda. Esto sí es gasto.',
        affectsCash: true,
        affectsCounterAccount: false,
        countsAsExpense: true,
        countsAsIncome: false,
        debtEffect: 0,
        requiresCategory: true,
        requiresCounterAccount: false,
        requiresDirection: false,
    },
    SPLIT_SETTLEMENT: {
        code: 'SPLIT_SETTLEMENT',
        label: 'Cobro de un gasto compartido',
        description: 'Entra dinero que te debían. No es un ingreso nuevo: solo recupera lo que ya pagaste.',
        affectsCash: true,
        affectsCounterAccount: false,
        countsAsExpense: false,
        countsAsIncome: false,
        debtEffect: 0,
        requiresCategory: false,
        requiresCounterAccount: false,
        requiresDirection: false,
    },
    ADJUSTMENT: {
        code: 'ADJUSTMENT',
        label: 'Ajuste de saldo',
        description: 'Corrige el saldo de una cuenta sin contarlo como gasto ni como ingreso. Se usa para el saldo inicial o para cuadrar con el banco.',
        affectsCash: true,
        affectsCounterAccount: false,
        countsAsExpense: false,
        countsAsIncome: false,
        debtEffect: 0,
        requiresCategory: false,
        requiresCounterAccount: false,
        requiresDirection: true,
    },
};
/** Todos los tipos, en el orden en que conviene mostrarlos. */
export const TRANSACTION_KINDS = [
    'EXPENSE',
    'INCOME',
    'TRANSFER',
    'FIADO_PURCHASE',
    'FIADO_PAYMENT',
    'DEBT_PAYMENT',
    'DEBT_INTEREST',
    'SPLIT_SETTLEMENT',
    'ADJUSTMENT',
];
/** Devuelve la semántica de un tipo. Lanza si el tipo no existe. */
export function kindSemantics(kind) {
    const found = SEMANTICS[kind];
    if (!found)
        throw new Error(`Tipo de movimiento desconocido: «${String(kind)}».`);
    return found;
}
/** ¿Es un tipo de movimiento válido? */
export function isTransactionKind(value) {
    return typeof value === 'string' && value in SEMANTICS;
}
/** Etiqueta legible de un tipo. */
export function kindLabel(kind) {
    return SEMANTICS[kind]?.label ?? kind;
}
/**
 * El importe con su signo, según lo que hace con tu dinero.
 *
 * ---------------------------------------------------------------------------
 *  Por qué está aquí y no en cada sitio
 * ---------------------------------------------------------------------------
 *
 * Jaime pidió ver **un gasto en negativo y un ingreso en positivo**, y que fuera así
 * «en los dos sitios, en todo»: en pantalla y en los archivos exportados.
 *
 * Si la pantalla lo calculara por su cuenta y el exportador por la suya, un día
 * discreparían, y entonces el archivo no cuadraría con lo que se ve. Esta función está
 * en el núcleo justamente para que haya **una sola respuesta**.
 *
 * Reglas:
 *
 *  · **Ingreso y cobro de un compartido**: positivo.
 *  · **Gasto, abono al fiado, abono a capital, intereses y transferencia**: negativo.
 *  · **Compra fiada**: negativo, porque es consumo —aunque el dinero no haya salido
 *    todavía—. Que no toque la caja se dice aparte, con `kindTouchesCash`.
 *  · **Ajuste**: según la dirección que indicó el usuario. Si no la indicó, cero.
 */
export function signedAmountForKind(kind, amount, cashDirection) {
    if (kind === 'INCOME' || kind === 'SPLIT_SETTLEMENT')
        return amount;
    if (kind === 'EXPENSE' ||
        kind === 'FIADO_PURCHASE' ||
        kind === 'FIADO_PAYMENT' ||
        kind === 'DEBT_PAYMENT' ||
        kind === 'DEBT_INTEREST' ||
        kind === 'TRANSFER') {
        return -amount;
    }
    if (kind === 'ADJUSTMENT') {
        if (cashDirection === 'IN')
            return amount;
        if (cashDirection === 'OUT')
            return -amount;
        return 0;
    }
    return amount;
}
/**
 * ¿Este movimiento movió dinero de una cuenta de verdad?
 *
 * La compra fiada es la excepción que importa: suma como gasto del mes, pero **no saca
 * dinero** hasta que se abona. Sin distinguirlo, sumar los importes daría un saldo que
 * no es el que hay en el bolsillo.
 */
export function kindTouchesCash(kind) {
    return kind !== 'FIADO_PURCHASE';
}
/**
 * Efecto **con signo** sobre el saldo de la cuenta de origen (o única).
 * Positivo suma, negativo resta, cero no la toca.
 */
export function cashDelta(input) {
    const semantics = kindSemantics(input.kind);
    if (!semantics.affectsCash)
        return 0;
    switch (input.kind) {
        case 'INCOME':
        case 'SPLIT_SETTLEMENT':
            return input.amount;
        case 'EXPENSE':
        case 'FIADO_PAYMENT':
        case 'DEBT_PAYMENT':
        case 'DEBT_INTEREST':
            return -input.amount;
        case 'TRANSFER':
            // Sale de la cuenta de origen y entra en la de destino.
            return -input.amount;
        case 'ADJUSTMENT':
            return input.direction === 'IN' ? input.amount : -input.amount;
        default:
            return 0;
    }
}
/**
 * Efecto **con signo** sobre la cuenta de destino.
 * Solo las transferencias mueven una segunda cuenta.
 */
export function counterAccountDelta(input) {
    const semantics = kindSemantics(input.kind);
    if (!semantics.affectsCounterAccount)
        return 0;
    return input.amount;
}
/** Efecto sobre la deuda o el fiado asociado: −1 la baja, +1 la sube, 0 no la toca. */
export function debtDelta(input) {
    const semantics = kindSemantics(input.kind);
    return semantics.debtEffect * input.amount;
}
/** ¿Este movimiento cuenta como gasto del período? */
export function countsAsExpense(kind) {
    return kindSemantics(kind).countsAsExpense;
}
/** ¿Este movimiento cuenta como ingreso del período? */
export function countsAsIncome(kind) {
    return kindSemantics(kind).countsAsIncome;
}
/** Categoría que se propone por defecto para un tipo de movimiento. */
export const DEFAULT_CATEGORY_BY_KIND = {
    DEBT_INTEREST: 'FINANCIAL',
    FIADO_PURCHASE: 'MARKET',
};
/**
 * Comprueba que un movimiento tenga todo lo que su tipo exige.
 *
 * Es la clase de validación que faltaba: en Cuentia se podía guardar una
 * transferencia sin cuenta de destino, o una compra fiada sin tienda, y el
 * movimiento quedaba roto sin que nadie lo notara.
 */
export function validateTransactionShape(input) {
    const semantics = kindSemantics(input.kind);
    const issues = [];
    if (!Number.isInteger(input.amount) || input.amount <= 0) {
        issues.push({ field: 'amount', message: 'El importe debe ser un entero mayor que cero.' });
    }
    if (semantics.affectsCash && !input.accountId) {
        issues.push({
            field: 'accountId',
            message: `Un movimiento de tipo «${semantics.label}» necesita una cuenta.`,
        });
    }
    if (semantics.requiresCounterAccount) {
        if (!input.toAccountId) {
            issues.push({
                field: 'toAccountId',
                message: 'Una transferencia necesita la cuenta de destino.',
            });
        }
        else if (input.toAccountId === input.accountId) {
            issues.push({
                field: 'toAccountId',
                message: 'La cuenta de destino no puede ser la misma que la de origen.',
            });
        }
    }
    // El desglose cubre la categoría si cada artículo trae la suya.
    const itemsCoverCategory = input.items !== undefined &&
        input.items.length > 0 &&
        input.items.every((item) => Boolean(item.categoryId));
    if (semantics.requiresCategory && !input.categoryId && !itemsCoverCategory) {
        issues.push({
            field: 'categoryId',
            message: input.items && input.items.length > 0
                ? 'Indica la categoría del movimiento, o ponle categoría a cada artículo del desglose.'
                : `Un movimiento de tipo «${semantics.label}» necesita una categoría.`,
        });
    }
    if (semantics.requiresDirection && !input.direction) {
        issues.push({
            field: 'direction',
            message: 'Un ajuste de saldo necesita indicar si el dinero entra o sale.',
        });
    }
    if (input.kind === 'FIADO_PURCHASE' || input.kind === 'FIADO_PAYMENT') {
        if (!input.fiadoStoreId) {
            issues.push({ field: 'fiadoStoreId', message: 'Indica a qué tienda corresponde el fiado.' });
        }
    }
    if (input.kind === 'DEBT_PAYMENT' || input.kind === 'DEBT_INTEREST') {
        if (!input.debtId) {
            issues.push({ field: 'debtId', message: 'Indica a qué deuda corresponde el pago.' });
        }
    }
    return issues;
}
/**
 * Comprueba que el desglose de un gasto dividido sume exactamente el total.
 *
 * Es una comprobación que en Cuentia no existía y que dejaba movimientos cuyo
 * total no cuadraba con sus artículos, descuadrando todos los reportes.
 */
export function validateItemsTotal(total, items) {
    if (items.length === 0)
        return [];
    const sum = items.reduce((acc, item) => acc + item.amount, 0);
    if (sum !== total) {
        return [
            {
                field: 'items',
                message: `La suma de los artículos (${sum.toLocaleString('es-CO')}) no coincide con el total ` +
                    `del movimiento (${total.toLocaleString('es-CO')}).`,
            },
        ];
    }
    return [];
}
//# sourceMappingURL=transactions-kind.js.map